import { faBell, faChevronLeft, faChevronRight, faCrown, faMapMarkedAlt, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence } from 'framer-motion';
import debounce from 'lodash.debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import placeholderImg from '../../assets/drawe-location-inmueble.png';
import { createAlert } from '../../services/alerts.service';
import TarjetaPropiedad from '../cards/TarjetaPropiedad';

const ListaExpandiblePropiedades = ({ propiedadesDestacadas = [], propiedadesRecomendadas = [], expanded, toggleExpand, searchId }) => {
  const contenedorRef = useRef(null);
  const [destacadas, setDestacadas] = useState(propiedadesDestacadas);
  const [recomendadas, setRecomendadas] = useState(propiedadesRecomendadas);
  const { token } = useSelector(state => state.auth);

  useEffect(() => {
    setDestacadas(propiedadesDestacadas);
    setRecomendadas(propiedadesRecomendadas); 
  }, [propiedadesDestacadas, propiedadesRecomendadas]);

  const handleRemoveDestacada = debounce((id) => {
    setDestacadas((prev) => prev.filter((propiedad) => propiedad.id !== id));
  }, 300);

  const handleRemoveRecomendada = debounce((id) => {
    setRecomendadas((prev) => prev.filter((propiedad) => propiedad.id !== id));
  }, 300);

  useEffect(() => {
    const ajustarAltura = () => {
      if (contenedorRef.current) {
        const navbar = document.querySelector('nav');
        const altura_navbar = navbar ? navbar.offsetHeight : 0;
        const altura_maxima = window.innerHeight - altura_navbar;
        contenedorRef.current.style.maxHeight = `${altura_maxima}px`;
      }
    };

    ajustarAltura();
    window.addEventListener('resize', ajustarAltura);
    window.addEventListener('load', ajustarAltura);

    return () => {
      window.removeEventListener('resize', ajustarAltura);
      window.removeEventListener('load', ajustarAltura);
    };
  }, []);

  const TarjetaPlaceholder = ({ searchId }) => {
    const [alertCreated, setAlertCreated] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [showInput, setShowInput] = useState(false);

    const handleCreateAlert = async () => {
      try {
        await createAlert(searchId, displayName, token); // Envía el searchId y displayName
        setAlertCreated(true);
      } catch (error) {
        console.error('Error al crear la alerta', error);
        setAlertCreated(true);
      }
    };

    const handleDisplayNameChange = (e) => {
      setDisplayName(e.target.value); // Actualiza el valor del displayName
    };

    const handleShowInput = () => {
      setShowInput(true); // Muestra el input para que el usuario ingrese el nombre
    };

    return (
      <div
        className={`p-4 py-8 rounded-lg shadow-xl flex flex-col items-center justify-center text-center transition-transform transform hover:scale-110 hover:shadow-2xl ${alertCreated ? 'bg-green-500' : 'bg-gradient-to-r from-cyan-200 via-cyan-400 to-cyan-500'
          }`}
      >
        {alertCreated ? (
          <>
            <FontAwesomeIcon
              icon={faBell}
              className="text-white text-6xl mb-4 animate-bounce"
            />
            <span className="text-white text-lg font-bold drop-shadow-md mb-4">
              Alerta creada con éxito
            </span>
          </>
        ) : (
          <>
            <img
              src={placeholderImg}
              alt="No se encontraron propiedades"
              className="w-24 h-24 mb-4 rounded-full border-4 border-white shadow-md"
            />
            <span className="text-white text-lg font-bold drop-shadow-md mb-4">
              ¿No encontraste lo que buscabas?
            </span>
            {showInput ? (
              <>
                <input
                  type="text"
                  value={displayName}
                  onChange={handleDisplayNameChange}
                  placeholder="Nombre de la alerta"
                  className="mb-4 p-2 rounded-lg border-2 border-cyan-500"
                />
                <button
                  className="text-sm bg-white text-cyan-500 font-bold py-2 px-4 rounded-full flex items-center space-x-2 transition duration-300 transform hover:scale-110"
                  onClick={handleCreateAlert}
                >
                  Guardar Alerta
                </button>
              </>
            ) : (
              <button
                className="text-sm bg-white text-cyan-500 font-bold py-2 px-4 rounded-full flex items-center space-x-2 transition duration-300 transform hover:scale-110"
                onClick={handleShowInput}
              >
                Crear Alerta
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const recomendadas_filtradas = recomendadas.filter(prop => !prop.is_highlighted);

  return (
    <div className={`bg-white relative transition-transform duration-300 transform ${expanded ? 'w-full md:w-8/12' : 'w-0 md:w-3/12'}`}>
      <div ref={contenedorRef} className={`relative items-center overflow-y-scroll p-6 flex flex-col gap-6 ${expanded ? '' : 'hidden md:flex'}`}>
        {destacadas.length > 0 && (
          <>
            <div className="flex items-center mt-2">
              <FontAwesomeIcon icon={faCrown} className="h-6 w-6 text-yellow-500 mr-2" />
              <h2 className="text-lg font-bold">Lo mejor en la zona</h2>
            </div>
            <div className={`w-full grid gap-6 ${expanded ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
              <AnimatePresence>
                {destacadas.map((propiedad) => (
                  <TarjetaPropiedad key={propiedad.id + 'destacado'} propiedad={propiedad} onRemove={handleRemoveDestacada} />
              ))}
              </AnimatePresence>
            </div>
          </>
        )}
        <div className="flex items-center mb-4 mt-6">
          <FontAwesomeIcon icon={faThumbsUp} className="h-6 w-6 text-green-500 mr-2" />
          <h2 className="text-lg font-bold">Propiedades Recomendadas</h2>
        </div>
        <div className={`grid w-full gap-6 ${expanded ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
          <AnimatePresence>
            <TarjetaPlaceholder searchId={searchId} />
            {recomendadas_filtradas.length > 0 ? (
              recomendadas_filtradas.map((propiedad) => (
                <TarjetaPropiedad key={propiedad.id} propiedad={propiedad} onRemove={handleRemoveRecomendada} />
              ))
            ) : (
              <div></div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {expanded && (
        <div className="fixed bottom-4 right-4 z-10 md:hidden">
          <button
            onClick={toggleExpand}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-transform
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none
                      disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 rounded-full p-2 bg-white shadow-lg transform hover:scale-105"
          >
            <FontAwesomeIcon icon={faMapMarkedAlt} className="h-6 w-6 text-gray-500 dark:text-gray-400 transition-transform transform hover:-translate-x-[1px]" />
            <span className="ml-2">Volver al mapa</span>
          </button>
        </div>
      )}
      <div className={`fixed top-[7rem] md:top-[12.7rem] ${expanded ? 'left-[-3.2rem]' : 'left-[-8rem]'}  transform translate-y-1/2 z-10`}>
        <button
          onClick={toggleExpand}
          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-transform
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none
                      disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 rounded-lg p-2 bg-white shadow-lg transform hover:scale-105"
          aria-label={expanded ? "Expandir lista" : "Colapsar lista"}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronRight : faChevronLeft}
            className="h-4 w-6 text-gray-500 dark:text-gray-400 transition-transform transform hover:-translate-x-[1px]"
            aria-hidden="true"
            role="img"
          />
          <p>{expanded ? '' : 'Mostrar lista'}</p>
        </button>
      </div>
    </div>
  );
};

export default ListaExpandiblePropiedades;
